import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import Button from "../../Button";
import Icon from "../../../componentsReusable/Icon";
import classnames from "classnames";
import {eq} from "lodash";
import Tooltip from "../../Tooltip";
import {useTranslation} from "react-i18next";
import {Alert, Badge, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import appUserRoutes from "../../../appUserRoutes";
import Link from "../../Application/Link";
import {
  ACTION_RECEIPT_FISCAL_NOT_FOUND,
  ACTION_RECEIPT_FISCAL_PRINT,
  ACTION_RECEIPT_FISCAL_PRINTED_ERROR,
  ACTION_RECEIPT_FISCAL_PRINTED_SUCCESS,
  ACTION_RECEIPT_FISCAL_PRINTER_CONNECTION_FAILED,
  ACTION_RECEIPT_FISCAL_PRINTER_NOT_CONNECTED,
  ACTION_RECEIPT_FISCAL_PRINTER_NOT_FOUND,
  ACTION_RECEIPT_FISCAL_PRINTING,
  useWebSocket,
} from "../../../contexts/WebSocketContext";
import getDefaultBsxPrinterAuthId from "../../../utils/BsxPrinterAuth/getDefaultBsxPrinterAuthId";
import {useSelector} from "react-redux";
import BsxPrinterAuthTable from "../../Settings/Integration/BsxPrinter/BsxPrinterAuthTable";

export const STATUS_IN_QUEUE = '0';
export const STATUS_PRINTING = '1';
export const STATUS_PRINTED = '2';
export const STATUS_ERROR = '5';

const ReceiptFiscalPrintButtonWebSocket = props => {
  const { t } = useTranslation('receipt_fiscal_print_button')
  const { sendMessage, addListener, removeListener } = useWebSocket();
  const [bsxStatus, setBsxStatus] = React.useState(props.saleDocument.bsxStatus);
  const [bsxErrorStr, setBsxErrorStr] = React.useState(props.saleDocument.bsxErrorStr);
  const [printing, setPrinting] = React.useState(props.saleDocument.bsxPrinting || [STATUS_IN_QUEUE, STATUS_PRINTING].includes(props.saleDocument.bsxStatus));

  const [showAuthTableModal, setShowAuthTableModal] = React.useState(false);
  const [showNoAuthModal, setShowNoAuthModal] = React.useState(false);

  const bsxPrinterAuths = useSelector(state => state.bsxPrinterAuths);

  useEffect(() => {
    const listeners = [
      addListener(`${ACTION_RECEIPT_FISCAL_PRINTING}.${props.saleDocument.id}`, ({saleDocumentId}) => {
        if(!eq(props.saleDocument.id, saleDocumentId)){
          return;
        }
        setPrinting(true);
      }),
      addListener(`${ACTION_RECEIPT_FISCAL_PRINTED_SUCCESS}.${props.saleDocument.id}`, ({saleDocumentId, bsxStatus}) => {
        if(!eq(props.saleDocument.id, saleDocumentId)){
          return;
        }
        setBsxStatus(bsxStatus.toString());
        setBsxErrorStr(null);
        setPrinting(false);
      }),
      addListener(`${ACTION_RECEIPT_FISCAL_PRINTED_ERROR}.${props.saleDocument.id}`, ({saleDocumentId, bsxStatus, bsxErrorStr}) => {
        if(!eq(props.saleDocument.id, saleDocumentId)){
          return;
        }
        setBsxStatus(bsxStatus.toString());
        setBsxErrorStr(bsxErrorStr.toString());
        setPrinting(false);
      }),
      addListener(`${ACTION_RECEIPT_FISCAL_PRINTER_NOT_FOUND}.${props.saleDocument.id}`, ({saleDocumentId}) => {
        if(!eq(props.saleDocument.id, saleDocumentId)){
          return;
        }
        setBsxStatus(STATUS_ERROR);
        setPrinting(false);
      }),
      addListener(`${ACTION_RECEIPT_FISCAL_PRINTER_NOT_CONNECTED}.${props.saleDocument.id}`, ({saleDocumentId}) => {
        if(!eq(props.saleDocument.id, saleDocumentId)){
          return;
        }
        setBsxStatus(STATUS_ERROR);
        setPrinting(false);
      }),
      addListener(`${ACTION_RECEIPT_FISCAL_PRINTER_CONNECTION_FAILED}.${props.saleDocument.id}`, ({saleDocumentId}) => {
        if(!eq(props.saleDocument.id, saleDocumentId)){
          return;
        }
        setBsxStatus(STATUS_ERROR);
        setPrinting(false);
      }),
      addListener(`${ACTION_RECEIPT_FISCAL_NOT_FOUND}.${props.saleDocument.id}`, ({saleDocumentId}) => {
        if(!eq(props.saleDocument.id, saleDocumentId)){
          return;
        }
        setBsxStatus(STATUS_ERROR);
        setPrinting(false);
      }),
    ]

    return () => {
      listeners.forEach(removeListener);
    };
  }, [addListener, removeListener, sendMessage]);


  const print = () => {
    if (printing || isPrinted()){
      return;
    }

    const bsxPrinterAuthId = getDefaultBsxPrinterAuthId()

    if(!bsxPrinterAuthId){
      if(bsxPrinterAuths.length === 0){
        setShowNoAuthModal(true);
        return;
      }
      if(bsxPrinterAuths.length > 1){
        setShowAuthTableModal(true);
        return;
      }

      return;
    }

    sendMessage({
      action: ACTION_RECEIPT_FISCAL_PRINT,
      saleDocumentId: props.saleDocument.id,
      bsxPrinterAuthId,
    })

    setPrinting(true)
  }

  const isPrinted = () => {
    return bsxStatus === STATUS_PRINTED
  }

  const getColor = () => {
    if(isPrinted()){
      return 'success'
    }

    if(printing){
      return 'secondary'
    }

    if([STATUS_ERROR].includes(bsxStatus)){
      return 'danger'
    }

    return 'warning'
  }

  const getTooltip = () => {
    if(isPrinted()){
      return t('tooltip_printed')
    }

    if(printing){
      return t('tooltip_printing')
    }

    if([STATUS_ERROR].includes(bsxStatus)){
      let msg = [t('tooltip_print_error')]

      if(bsxErrorStr){
        msg.push("<br /><br />" + t('tooltip_print_error_msg', {error: bsxErrorStr}))
      }

      return <span dangerouslySetInnerHTML={{__html: msg.join('')}} />
    }

    return t('tooltip_print')
  }

  return (
    <div id={`sale-document-${props.saleDocument.id}-printBtn-new-tooltip`} className="ml-2">
      {
        props.presentationType === 'icon'
          ? <Icon
            tooltip={getTooltip()}
            className={classnames({
              'fas fa-receipt': !printing,
              'fas fa-spin fa-spinner': printing,
              [`text-${getColor()}`]: true,
              'cursor-pointer': !isPrinted(),
            })}
            onClick={print}
          />
          : <Button
            size="sm"
            color={getColor()}
            disabled={isPrinted() || printing}
            onClick={print}
            loading={printing}
          >
            <i className="fas fa-receipt"/><span>{isPrinted() ? t('printed') : t('to_print')}</span>
          </Button>
      }
      <Tooltip id={`sale-document-${props.saleDocument.id}-printBtn-new-tooltip`} msg={getTooltip()}/>

      {showAuthTableModal && <Modal isOpen toggle={() => setShowAuthTableModal(false)} className="modal-lg">
        <ModalHeader toggle={() => setShowAuthTableModal(false)}>Wybierz drukarkę</ModalHeader>
        <ModalBody>
          <Alert color="info">
            <span>Wybierz jedną z poniższych drukarek jako domyślną dla tego urządzenia</span>
          </Alert>
          <BsxPrinterAuthTable
            addButton={false}
            removeButton={false}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowAuthTableModal(false)}>Zamknij</Button>
        </ModalFooter>
      </Modal>}

      {showNoAuthModal && <Modal isOpen toggle={() => setShowNoAuthModal(false)}>
        <ModalHeader toggle={() => setShowNoAuthModal(false)}>Brak autoryzacji</ModalHeader>
        <ModalBody>
          Nie masz podłączonej żadnej drukarki fiskalnej. Przejdź do <Link to={appUserRoutes.settings.integrations}><Badge>Ustawienia{`->`}Inne{`->`}Integracje{`->`}Moduł fiskalny</Badge></Link> aby przeprowadzić integrację.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowNoAuthModal(false)}>Zamknij</Button>
        </ModalFooter>
      </Modal>}
    </div>
  );
};

ReceiptFiscalPrintButtonWebSocket.defaultProps = {
  presentationType: 'button'
};

ReceiptFiscalPrintButtonWebSocket.propTypes = {
  presentationType: PropTypes.oneOf(['button', 'icon']).isRequired,
  saleDocument: PropTypes.object.isRequired
};

export default ReceiptFiscalPrintButtonWebSocket;
